import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
  ActionsMenu,
} from '@elka/ui';
import {
  Briefcase,
  Building2,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  Copy,
  SearchX,
} from 'lucide-react';
import type { Timestamp } from 'firebase/firestore';
import { formatNumberWithCommas } from '@elka/utils';
import { useIsMobile } from '@elka/hooks';
import type { DealWithQuotingStatus } from '../hooks/useDealsWithQuotingStatus';
import { DealsCardView } from './DealsCardView';
import { DuplicateDealModal } from './DuplicateDealModal';
import { QuotingProgressDots } from '../../pricing-workspace/components/QuotingProgressDots';
import { NextActionChip } from '../../pricing-workspace/components/NextActionChip';
import { deriveNextAction, nextActionPath } from '../../pricing-workspace/lib/nextAction';

interface DealsTableProps {
  deals: DealWithQuotingStatus[];
}

type SortField =
  | 'name'
  | 'companyName'
  | 'stage'
  | 'quotingStatus'
  | 'value'
  | 'quoteTotal'
  | 'createdAt'
  | 'updatedAt';
type SortDirection = 'asc' | 'desc';

function toDateStr(ts: Timestamp | undefined): string {
  if (!ts || !ts.toDate) return '—';
  const d = ts.toDate();
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function SortIcon({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) {
  if (sortField !== field)
    return <ChevronsUpDown size={13} className="text-gray-300 opacity-0 group-hover:opacity-100" />;
  return sortDirection === 'asc' ? (
    <ChevronUp size={13} className="text-[#3C5FA8]" />
  ) : (
    <ChevronDown size={13} className="text-[#3C5FA8]" />
  );
}

function SortableHead({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}) {
  return (
    <TableHead className="group cursor-pointer select-none" onClick={() => onSort(field)}>
      <div className="flex items-center gap-1 transition-colors hover:text-[#0C1A55]">
        {label}
        <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
      </div>
    </TableHead>
  );
}

export function DealsTable({ deals }: DealsTableProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [duplicating, setDuplicating] = useState<DealWithQuotingStatus | null>(null);

  const sortedDeals = useMemo(() => {
    return [...deals].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'companyName':
          comparison = a.companyName.localeCompare(b.companyName);
          break;
        case 'stage':
          comparison = a.stage.localeCompare(b.stage);
          break;
        case 'quotingStatus':
          comparison = a.quotingStatus.localeCompare(b.quotingStatus);
          break;
        case 'value':
          comparison = (a.value || 0) - (b.value || 0);
          break;
        case 'quoteTotal':
          comparison = (a.quoteTotal || 0) - (b.quoteTotal || 0);
          break;
        case 'createdAt':
          comparison = (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
          break;
        case 'updatedAt':
          comparison =
            (a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0) -
            (b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [deals, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (deals.length === 0) {
    return <EmptyState dense icon={<SearchX size={28} />} title="אין עסקאות" />;
  }

  if (isMobile) {
    return <DealsCardView deals={sortedDeals} onDealClick={(id) => navigate(`/deals/${id}`)} />;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHead
              field="name"
              label="שם העסקה"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHead
              field="companyName"
              label="לקוח"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHead
              field="quotingStatus"
              label="התקדמות"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <TableHead>פעולה הבאה</TableHead>
            <SortableHead
              field="value"
              label="שווי עסקה"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHead
              field="quoteTotal"
              label="סכום הצעה"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHead
              field="createdAt"
              label="נוצר"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHead
              field="updatedAt"
              label="עודכן"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDeals.map((deal, index) => (
            <TableRow
              key={deal.id}
              index={index}
              isClickable
              onClick={() => navigate(`/deals/${deal.id}`)}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <Briefcase size={15} className="flex-shrink-0 text-[#3C5FA8]" />
                  <span className="font-semibold text-gray-900">{deal.name}</span>
                  <div className="ms-auto" onClick={(e) => e.stopPropagation()}>
                    <ActionsMenu
                      actions={[
                        {
                          icon: <Copy size={14} />,
                          label: 'שכפל',
                          onClick: () => setDuplicating(deal),
                        },
                      ]}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Building2 size={13} className="text-gray-400" />
                  <span>{deal.companyName}</span>
                </div>
              </TableCell>
              <TableCell>
                <QuotingProgressDots status={deal.quotingStatus} />
              </TableCell>
              <TableCell>
                <NextActionChip
                  action={deriveNextAction({
                    stage: deal.stage,
                    quotingStatus: deal.quotingStatus,
                    updatedAt: deal.updatedAt,
                  })}
                  onClick={() =>
                    navigate(
                      nextActionPath(
                        deal.id,
                        deriveNextAction({
                          stage: deal.stage,
                          quotingStatus: deal.quotingStatus,
                          updatedAt: deal.updatedAt,
                        }).target
                      )
                    )
                  }
                />
              </TableCell>
              <TableCell>
                {deal.value != null && deal.value > 0 ? (
                  <span className="font-medium text-gray-700 tabular-nums">{`₪${formatNumberWithCommas(String(Math.round(deal.value)))}`}</span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </TableCell>
              <TableCell>
                {deal.quoteTotal != null && deal.quoteTotal > 0 ? (
                  <span className="font-semibold text-[#0C1A55] tabular-nums">{`₪${formatNumberWithCommas(String(Math.round(deal.quoteTotal)))}`}</span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </TableCell>
              <TableCell>
                <span className="font-mono text-xs text-gray-400">{toDateStr(deal.createdAt)}</span>
              </TableCell>
              <TableCell>
                <span className="font-mono text-xs text-gray-400">{toDateStr(deal.updatedAt)}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {duplicating && (
        <DuplicateDealModal isOpen onClose={() => setDuplicating(null)} deal={duplicating} />
      )}
    </>
  );
}
